import React, { useEffect, useState } from 'react';
import useFormStatus from '../../Hooks/useFormStatus';
import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { GET_ENTERPRISE_DETAILS, UPDATE_ENTERPRISE } from '../../urls';
import LoaderCircle from '../LoaderCircle';
import { Button, Input, Typography, Checkbox, Select, showToast, Tooltip } from '@attentive-platform/stem-ui';
import {
  transformContractData,
  setContractInfoData,
  setInitialFormData,
  isValidPostalCode
} from '../../Utils/HelperFunctions';
import {
  GROUP_INPUT_DATA,
  CHECHBOX_FIELDS,
  BILLING_FREQUENCY,
  BILLING_FREQUENCY_ENUM,
  COUNTRY_CODE
} from '../../Constants/Constants';
import SearchComponent from '../SearchComponent';
import { GET_OPTIONAL_FEATURES } from '../../urls';

const UpdateContract = () => {
  const [email, setEmail] = useState('');
  const [showEnterpriseForm, setShowEnterpriseForm] = useState(false);

  // Enterprise data
  const [enterpriseAttributes, setEnterpriseAttributes] = useState();
  const [billingDetails, setBillingDetails] = useState();
  const [enterpriseContract, setEnterpriseContract] = useState();
  const [platformAccess, setPlatformAccess] = useState();
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState();
  const [enterpriseOptionalFeatures, setEnterpriseOptionalFeatures] = useState();

  // Billing info state
  const [contractInfo, setContractInfo] = useState({});
  const [isInvalidZip, setIsInvalidZip] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Enterprise conversion form state
  const [formData, setFormData] = useState({});
  const [defaultReport, setDefaultReport] = useState('');
  const [inputValue, setInputValue] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionalFeatures, setOptionalFeatures] = useState([]);

  const { successMessage, errorMessage, handleApiResult } = useFormStatus();

  // Fetch optional features
  useEffect(() => {
    const fetchOptionalFeatures = async () => {
      if (showEnterpriseForm) {
        const response = await fetchApiData({
          endpoint: GET_OPTIONAL_FEATURES,
          apiRequest: 'GET'
        });

        const existingFeatures = enterpriseOptionalFeatures || [];
        const newFeatures = response?.data || [];

        const existingFeaturesMap = new Map(existingFeatures.map(feature => [feature.identifier, feature]));

        const mergedFeatures = [...existingFeatures];
        newFeatures.forEach(newFeature => {
          if (!existingFeaturesMap.has(newFeature.identifier)) {
            mergedFeatures.push({
              ...newFeature,
              is_selected: false
            });
          }
        });

        setOptionalFeatures(mergedFeatures);
      }
    };
    fetchOptionalFeatures();
  }, [showEnterpriseForm, enterpriseOptionalFeatures]);

  // Initialize form data and contract info when data is loaded
  useEffect(() => {
    if (showEnterpriseForm && enterpriseAttributes && platformAccess) {
      const initialData = setInitialFormData({
        email,
        enterpriseAttributes,
        platformAccess
      });
      setDefaultReport(initialData?.default_report);
      setFormData({
        ...initialData,
        total_users: totalUsers
      });
      const selectedOptions = initialData?.reports_given?.map(report => {
        const label = report?.name;
        const value = report?.id;
        return { label, value };
      });
      setSelectedOptions(selectedOptions);
      setInputValue(selectedOptions.map(option => option?.value));
    }
  }, [showEnterpriseForm, email, enterpriseAttributes, platformAccess, totalUsers, enterpriseContract]);

  useEffect(() => {
    if (showEnterpriseForm && enterpriseContract && billingDetails) {
      const initData = setContractInfoData({
        enterpriseContract,
        billingDetails
      });
      setContractInfo(initData);
    }
  }, [showEnterpriseForm, enterpriseContract, billingDetails]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    const encodedMail = encodeURIComponent(email.toLowerCase());
    const url = interpolate(GET_ENTERPRISE_DETAILS, [encodedMail]);
    const response = await fetchApiData({
      endpoint: url,
      apiRequest: 'GET'
    });

    const data = response?.data;

    handleApiResult(response);

    if (response.error === null) {
      setTotalUsers(data?.total_users);
      setEnterpriseAttributes(data?.enterprise_attributes);
      setEnterpriseContract(transformContractData(data?.enterprise_contract || {}, 'centsToDollar'));
      setBillingDetails(data?.billing_details || {});
      setPlatformAccess(data?.platform_access);
      setEnterpriseOptionalFeatures(data?.optional_feature_costs);
      setShowEnterpriseForm(true);
    }
    setLoading(false);
  };

  const showBillingInfo = !formData?.is_pilot;

  const handleUpdateContract = async e => {
    e.preventDefault();
    if (!showEnterpriseForm) {
      return;
    }
    if (showBillingInfo && !isValidPostalCode(contractInfo.zip, contractInfo.country)) {
      window.scrollTo(0, 0);
      setIsInvalidZip(true);
      return;
    }

    setSubmitting(true);
    setIsInvalidZip(false);

    const { end_date, total_users, email: formDataEmail, ...ent_attributes } = formData;
    const {
      address,
      city,
      zip,
      country,
      addendum_sheet_cost,
      billing_email,
      billing_frequency,
      bpt_contract_amount,
      company_name,
      manual_worksheet_cost,
      start_date: contractStartDate,
      end_date: contractEndDate,
      workable_sheet_cost
    } = transformContractData(contractInfo);

    const data = {
      billing_details: showBillingInfo
        ? {
            name: company_name,
            address,
            city,
            zip,
            country,
            email: billing_email?.toLowerCase()
          }
        : null,
      email: formDataEmail?.toLowerCase(),
      total_users: parseFloat(total_users || 0),
      enterprise_attributes: {
        ...ent_attributes,
        reports_given: inputValue,
        default_report: defaultReport
      },
      optional_feature_costs: optionalFeatures,
      platform_access: {
        end_date
      },
      enterprise_contract: showBillingInfo
        ? {
            addendum_sheet_cost,
            billing_frequency,
            bpt_contract_amount,
            manual_worksheet_cost,
            start_date: contractStartDate + 'T00:00:00+05:30',
            end_date: contractEndDate + 'T00:00:00+05:30',
            workable_sheet_cost
          }
        : null
    };

    const res = await fetchApiData({
      endpoint: UPDATE_ENTERPRISE,
      data
    });

    handleApiResult(res);

    if (res.error === null) {
      showToast('Contract updated successfully', 'success');
    } else if (res.error) {
      showToast(res.error, 'error');
    }

    setSubmitting(false);
  };

  return (
    <div className='flex gap-6 w-full'>
      {/* left section */}
      <div
        className={`w-1/2 bg-white rounded-2xl shadow-md p-6 ${showEnterpriseForm ? 'h-[600px] overflow-y-auto' : 'h-[200px]'}`}>
        <div className='border-b border-gray-200 pb-2 mb-3'>
          <Typography variant='h6-medium'>Account/Update Contract</Typography>
        </div>
        {(errorMessage || successMessage) && (
          <div className={`alert ${errorMessage ? 'alert-danger' : 'alert-success'} mb-3`} role='alert'>
            <Typography variant='body8'>{errorMessage || successMessage}</Typography>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className='mb-4'>
            <Input
              label='Email'
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder='Enter email address'
              fullWidth
            />
          </div>
          <div className='flex justify-end mb-4'>
            <Button
              type='submit'
              color='primary'
              variant='contained'
              size='medium'
              disabled={loading}
              className='!text-base'>
              {loading ? <LoaderCircle /> : 'Next'}
            </Button>
          </div>
        </form>

        {showEnterpriseForm && (
          <form onSubmit={handleUpdateContract}>
            <div className='border-t border-gray-200 pt-4'>
              <SimplifiedEnterpriseConversion
                formData={formData}
                setFormData={setFormData}
                setContractInfo={setContractInfo}
                contractInfo={contractInfo}
                isPilot={enterpriseAttributes?.is_pilot}
                setEmail={setEmail}
                defaultReport={defaultReport}
                setDefaultReport={setDefaultReport}
                inputValue={inputValue}
                setInputValue={setInputValue}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                optionalFeatures={optionalFeatures}
                setOptionalFeatures={setOptionalFeatures}
              />
            </div>

            {/* Update Contract button - shown when form is displayed but billing info is not */}
            {!showBillingInfo && (
              <div className='flex justify-end mt-6'>
                <Button
                  type='submit'
                  color='primary'
                  variant='contained'
                  size='large'
                  disabled={submitting}
                  className='!text-base'>
                  {submitting ? <LoaderCircle /> : 'Update Contract'}
                </Button>
              </div>
            )}
          </form>
        )}
      </div>

      {/* right section (billing info) */}
      {showEnterpriseForm && showBillingInfo && (
        <div className='flex-1 bg-white rounded-2xl shadow-md p-6'>
          <form onSubmit={handleUpdateContract}>
            <div className='border-b border-gray-200 pb-2 mb-3'>
              <Typography variant='h6-medium'>Enterprise Billing Info</Typography>
            </div>
            <SimplifiedEnterpriseBillingInfo
              successMessage={successMessage}
              errorMessage={errorMessage}
              isInvalidZip={isInvalidZip}
              contractInfo={contractInfo}
              setContractInfo={setContractInfo}
              submitting={submitting}
            />
          </form>
        </div>
      )}
    </div>
  );
};

// simplified enterprise conversion component
const SimplifiedEnterpriseConversion = ({
  formData,
  setFormData,
  setContractInfo,
  contractInfo,
  isPilot,
  setEmail,
  defaultReport,
  setDefaultReport,
  inputValue,
  setInputValue,
  selectedOptions,
  setSelectedOptions,
  optionalFeatures,
  setOptionalFeatures
}) => {
  const handleDataChange = (e, field) => {
    const val = e.target.value;
    setFormData({ ...formData, [field]: val });
    if (field === 'email') {
      if (setEmail) setEmail(val);
      setContractInfo({ ...contractInfo, email: val });
    }
  };

  const handleCheckboxChange = (e, field) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  return (
    <div className='w-full'>
      <div className='mb-3'>
        <Input
          label='Admin email'
          type='email'
          fullWidth
          value={formData?.email || ''}
          onChange={e => handleDataChange(e, 'email')}
          placeholder='Enter admin email'
        />
      </div>
      <div className='mb-4'>
        <SearchComponent
          setInputValue={setInputValue}
          setDefaultReport={setDefaultReport}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          defaultReport={defaultReport}
        />
      </div>
      {GROUP_INPUT_DATA.map(({ key, inputs, showLabel }) => {
        const isTwoColumn = inputs.length === 2;

        return (
          <div key={key} className={isTwoColumn ? 'grid grid-cols-2 gap-4 mb-3' : 'mb-3'}>
            {inputs.map(({ valueKey, type, label, placeholder, ...props }) => {
              if (type === 'date') {
                return (
                  <div key={valueKey} className='grid grid-cols-[2fr_1fr] items-center gap-4 mb-3'>
                    <Typography variant='body8' className='text-sm font-medium text-gray-700'>
                      {label || placeholder}
                    </Typography>
                    <Input
                      type='date'
                      fullWidth
                      value={formData[valueKey] || ''}
                      onChange={e => handleDataChange(e, valueKey)}
                      {...props}
                      placeholder='-'
                    />
                  </div>
                );
              }

              const isWideInput = valueKey === 'subdomain' || valueKey === 'callback_url';
              const inputValue = formData[valueKey] || '';
              const showTooltip = isWideInput && inputValue;

              return (
                <div
                  key={valueKey}
                  className={`grid ${isWideInput ? 'grid-cols-[1fr_1fr]' : 'grid-cols-[3fr_2fr]'} items-center gap-2 `}>
                  <Typography variant='body8'>{label || placeholder}</Typography>
                  {showTooltip ? (
                    <Tooltip title={inputValue} placement='top'>
                      <div className='w-full'>
                        <Input
                          type={type}
                          fullWidth
                          value={inputValue}
                          onChange={e => handleDataChange(e, valueKey)}
                          {...props}
                          placeholder={isWideInput ? '-' : ''}
                        />
                      </div>
                    </Tooltip>
                  ) : (
                    <Input
                      type={type}
                      fullWidth
                      value={inputValue}
                      onChange={e => handleDataChange(e, valueKey)}
                      {...props}
                      placeholder={isWideInput ? '-' : ''}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {optionalFeatures && optionalFeatures.length > 0 && (
        <div className='border rounded mb-3 bg-white max-h-[200px] overflow-y-scroll'>
          <div className='flex items-center justify-between font-semibold border-b pl-2 bg-gray-100 rounded-t mb-2'>
            <div className='flex items-center'>
              <Checkbox
                checked={optionalFeatures.every(feature => feature.is_selected)}
                onChange={e => {
                  const updated = optionalFeatures.map(feature => ({
                    ...feature,
                    is_selected: e.target.checked
                  }));
                  setOptionalFeatures(updated);
                }}
                className='!mr-0 !pr-0'
              />
              <Typography variant='body8' className='ml-2'>
                Features
              </Typography>
            </div>
            <div className='w-1/4 flex items-center'>
              <Typography variant='body8'>Pricing</Typography>
            </div>
          </div>
          <div className='pl-2'>
            {optionalFeatures.map((feature, index) => (
              <div className='flex items-center justify-between ' key={feature.identifier}>
                <div className='flex items-center mb-2'>
                  <Checkbox
                    checked={feature.is_selected}
                    onChange={e => {
                      const updated = [...optionalFeatures];
                      updated[index].is_selected = e.target.checked;
                      setOptionalFeatures(updated);
                    }}
                    className='mr-0 pr-0'
                  />
                  <Typography variant='body8' className='ml-2'>
                    {feature.name}
                  </Typography>
                </div>
                <div className='w-1/4 flex items-center'>
                  <Input
                    type='number'
                    value={feature.cost}
                    onChange={e => {
                      const updated = [...optionalFeatures];
                      updated[index].cost = e.target.value;
                      setOptionalFeatures(updated);
                    }}
                  />
                  <Typography variant='body8' className='mr-3 ml-2'>
                    $
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className='flex flex-wrap mb-3'>
        {CHECHBOX_FIELDS.map((field, index) => (
          <div className={`flex items-center ${index > 0 ? 'ml-4' : ''}`} key={field}>
            <Checkbox
              checked={formData[field] || false}
              onChange={e => handleCheckboxChange(e, field)}
              disabled={field === 'is_pilot' && !isPilot}
              className='mr-0 pr-0'
            />
            <Typography variant='body8'>
              {field.replace(/_/g, ' ').charAt(0).toUpperCase() + field.replace(/_/g, ' ').slice(1)}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

// simplified enterprise billing info component
const SimplifiedEnterpriseBillingInfo = ({
  errorMessage,
  successMessage,
  isInvalidZip,
  contractInfo,
  setContractInfo,
  submitting
}) => {
  const handleDataChange = (val, field) => {
    setContractInfo(prev => ({ ...prev, [field]: val }));
  };

  const setDates = date => {
    const year = parseInt(date?.substring(0, 4)) + 1;
    const endDate = year + date?.substring(4);
    setContractInfo({ ...contractInfo, start_date: date, end_date: endDate });
  };

  const countryItems = [
    { label: 'United States', value: COUNTRY_CODE.USA },
    { label: 'Canada', value: COUNTRY_CODE.CANADA },
    { label: 'Australia', value: COUNTRY_CODE.AUSTRALIA }
  ];

  const billingFrequencyItems = [
    { label: BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.QUATERLY], value: BILLING_FREQUENCY_ENUM.QUATERLY },
    { label: BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.MONTHLY], value: BILLING_FREQUENCY_ENUM.MONTHLY },
    { label: BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.HALF_YEARLY], value: BILLING_FREQUENCY_ENUM.HALF_YEARLY },
    { label: BILLING_FREQUENCY[BILLING_FREQUENCY_ENUM.ANNUAL], value: BILLING_FREQUENCY_ENUM.ANNUAL }
  ];

  return (
    <div className='w-full'>
      {(errorMessage || successMessage || isInvalidZip) && (
        <div className={`alert ${errorMessage || isInvalidZip ? 'alert-danger' : 'alert-success'} mb-3`} role='alert'>
          <Typography variant='body8'>{errorMessage || successMessage || 'Please enter valid zip code'}</Typography>
        </div>
      )}

      {/* Billing Details */}
      <div className='grid grid-cols-2 gap-x-4 gap-y-2 mb-2'>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Company Name
          </Typography>
          <Input
            type='text'
            fullWidth
            value={contractInfo.company_name || ''}
            onChange={e => handleDataChange(e.target.value, 'company_name')}
            placeholder='Enter company name'
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Company Billing Email
          </Typography>
          <Input
            type='email'
            fullWidth
            value={contractInfo.billing_email || ''}
            onChange={e => handleDataChange(e.target.value, 'billing_email')}
            placeholder='Enter company billing email'
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Address
          </Typography>
          <Input
            type='text'
            fullWidth
            value={contractInfo.address || ''}
            onChange={e => handleDataChange(e.target.value, 'address')}
            placeholder='Enter address'
            required
          />
        </div>
        <div className='mb-1'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            City
          </Typography>
          <Input
            type='text'
            fullWidth
            value={contractInfo.city || ''}
            onChange={e => handleDataChange(e.target.value, 'city')}
            placeholder='Enter city'
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Zipcode
          </Typography>
          <Input
            type='text'
            fullWidth
            value={contractInfo.zip || ''}
            onChange={e => handleDataChange(e.target.value, 'zip')}
            placeholder='Enter zipcode'
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
            Country
          </Typography>
          <Select
            fullWidth
            value={contractInfo.country}
            onChange={value => handleDataChange(value, 'country')}
            items={countryItems}
            required
          />
        </div>
      </div>

      {/* Contract Info */}
      <Typography variant='h6-medium' className='mb-3'>
        Enterprise Contract Info
      </Typography>

      <div className='grid grid-cols-2 gap-4 mb-3'>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Contract Start Date
          </Typography>
          <Input
            type='date'
            fullWidth
            value={contractInfo.start_date || ''}
            onChange={e => {
              setDates(e.target.value);
            }}
            required
          />
        </div>
        <div className='mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Contract End Date
          </Typography>
          <Input
            type='date'
            fullWidth
            value={contractInfo.end_date || ''}
            onChange={e => {
              setContractInfo({ ...contractInfo, end_date: e.target.value });
            }}
            required
            placeholder='-'
          />
        </div>
      </div>

      <div className='mb-3'>
        <Typography variant='body8' className='text-sm font-medium text-gray-700'>
          Billing Frequency
        </Typography>
        <Select
          fullWidth
          value={contractInfo?.billing_frequency}
          onChange={value => handleDataChange(value, 'billing_frequency')}
          items={billingFrequencyItems}
          required
          placeholder='-'
        />
      </div>

      <div className='mb-3'>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Contract Amount for BPT ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.bpt_contract_amount || ''}
            onChange={e => handleDataChange(e.target.value, 'bpt_contract_amount')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Workable Sheet Cost BPT ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.workable_sheet_cost || ''}
            onChange={e => handleDataChange(e.target.value, 'workable_sheet_cost')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Addendum Sheet Cost BPT ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.addendum_sheet_cost || ''}
            onChange={e => handleDataChange(e.target.value, 'addendum_sheet_cost')}
            required
            placeholder='-'
          />
        </div>
        <div className='grid grid-cols-[4fr_1fr] items-center gap-2 mb-3'>
          <Typography variant='body8' className='text-sm font-medium text-gray-700'>
            Workable Sheet in Manual Measurement for BPT ($)
          </Typography>
          <Input
            type='number'
            step={0.01}
            min={1}
            fullWidth
            value={contractInfo.manual_worksheet_cost || ''}
            onChange={e => handleDataChange(e.target.value, 'manual_worksheet_cost')}
            required
            placeholder='-'
          />
        </div>
      </div>

      <div className='flex justify-end mt-6'>
        <Button
          type='submit'
          color='primary'
          variant='contained'
          size='large'
          disabled={submitting}
          className='!text-base'>
          {submitting ? <LoaderCircle /> : 'Update Contract'}
        </Button>
      </div>
    </div>
  );
};

export default UpdateContract;
